import { Box, Link as MuiLink } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DocSummary } from "@xop/dashboard-shared";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import { BulkCheckbox } from "../../../components/bulk-selection/index.js";
import { formatDocDate } from "../dateFormat.js";
import { RowActions, SolutionCell, StatusChip } from "./rowParts.js";

/**
 * One document `<li>` in the type/release list: the bulk checkbox (only when
 * the user may modify the doc), the title link to the reader, the inline
 * solution-area pill, the status / featured chips, and the ⋮ action menu. The
 * page owns the bulk-selection + mutation state and passes the per-row slices
 * + handlers down.
 */
export function DocRow({
  doc,
  canModify,
  solutionAreas,
  selected,
  onSelectChange,
  solutionBusy,
  onPickSolution,
  pdfBusy,
  onPdf,
  onPublish,
  onUnpublish,
  onDelete,
  onShareToChat,
  onMove,
}: {
  doc: DocSummary;
  canModify: boolean;
  solutionAreas: ReadonlyArray<{ id: number; slug: string; name: string }>;
  selected: boolean;
  onSelectChange: (checked: boolean, opts: { shiftKey: boolean }) => void;
  solutionBusy: boolean;
  onPickSolution: (ids: number[]) => void;
  pdfBusy: boolean;
  onPdf: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onShareToChat: () => void;
  onMove?: () => void;
}): JSX.Element {
  return (
    <Box
      component="li"
      data-testid={`docs-row-${doc.id}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        px: 2,
        // Compact, uniform rows. These are single-line rows; py:1.5 +
        // the small action buttons rendered ~55px, which read as
        // "abnormally tall" for a list. 44px (Material's min target)
        // is a normal dense row, and `minHeight` keeps every row
        // identical whether or not it carries the optional
        // "Featured" chip.
        minHeight: 44,
        py: 0.75,
        "&:last-of-type": { borderBottom: 0 },
        "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.6) },
      }}
    >
      {canModify && (
        <BulkCheckbox
          checked={selected}
          onChange={onSelectChange}
          ariaLabel={`Select ${doc.title}`}
        />
      )}
      <MuiLink
        component={RouterLink}
        to={`/documentation/library/${doc.slug}`}
        underline="hover"
        sx={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500, color: "text.primary" }}
      >
        {doc.title}
      </MuiLink>
      {/* Solution pill on EVERY row. Editors click it to re-tag the doc
          inline (a checkbox menu of the live pillars — a doc can carry
          several); viewers see a static summary pill. */}
      <SolutionCell
        doc={doc}
        areas={solutionAreas}
        editable={canModify}
        busy={solutionBusy}
        onPick={onPickSolution}
      />
      <StatusChip tone={doc.status === "published" ? "good" : "neutral"}>{doc.status}</StatusChip>
      {doc.featured && <StatusChip tone="warn">Featured</StatusChip>}
      {/* "Created" column — the doc's OWN date, detected from an uploaded PDF's
          embedded /CreationDate (NOT the upload time). "—" when none was
          detected. Sortable from the list header; hidden on the narrowest
          viewports to keep the row single-line. */}
      <Box
        data-testid={`docs-row-date-${doc.id}`}
        title={doc.document_date ? new Date(doc.document_date).toLocaleString() : "No document date detected"}
        sx={{
          width: 92,
          flexShrink: 0,
          textAlign: "right",
          fontSize: 12,
          color: "text.secondary",
          fontVariantNumeric: "tabular-nums",
          display: { xs: "none", sm: "block" },
        }}
      >
        {formatDocDate(doc.document_date)}
      </Box>
      {/* Every per-row action collapses into one ⋮ menu (View /
          Download PDF / Edit / Publish / Delete). Views + a separate
          PDF/View button were removed — the title opens the reader. */}
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
        <RowActions
          doc={doc}
          canModify={canModify}
          pdfBusy={pdfBusy}
          onPdf={onPdf}
          onPublish={onPublish}
          onUnpublish={onUnpublish}
          onDelete={onDelete}
          onShareToChat={onShareToChat}
          {...(onMove ? { onMove } : {})}
        />
      </Box>
    </Box>
  );
}
